import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import { Download, Copy, RefreshCw, QrCode } from 'lucide-react';
import { useTeamAccessCode } from '@/hooks/useTeamAccessCode';

interface TeamAccessCodeGeneratorProps {
  teamId: number;
  teamName: string;
}

interface AccessCode {
  id: string;
  team_id: number;
  access_code: string;
  qr_code_data: string | null;
  created_by: string;
  is_active: boolean;
  expires_at: string | null;
  max_uses: number | null;
  current_uses: number;
  created_at: string;
  updated_at: string;
}

export const TeamAccessCodeGenerator: React.FC<TeamAccessCodeGeneratorProps> = ({
  teamId,
  teamName
}) => {
  const [accessCode, setAccessCode] = useState<AccessCode | null>(null);
  const [loading, setLoading] = useState(false);
  const [maxUses, setMaxUses] = useState<string>('');
  const [expiresInDays, setExpiresInDays] = useState<string>('');
  
  const { generateCode } = useTeamAccessCode();

  const generateQRCode = async (code: string): Promise<string> => {
    try {
      const qrData = JSON.stringify({
        type: 'team_access_code',
        code,
        teamId,
        teamName
      });
      return await QRCode.toDataURL(qrData, {
        width: 512,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
    } catch (error) {
      console.error('Error generating QR code:', error);
      throw error;
    }
  };

  const generateAccessCode = async () => {
    setLoading(true);
    try {
      // Use the hook's generateCode function which now accepts teamId
      const result = await generateCode(teamId);
      
      if (!result) {
        throw new Error('Failed to generate access code');
      }

      console.log('Generate code result:', result);
      
      // Check if result is a string (success) or null (error already handled by hook)
      if (typeof result !== 'string') {
        return; // Error already shown by hook
      }

      const newCode = result;

      // Generate QR code
      const qrCodeData = await generateQRCode(newCode);

      // Calculate expiration date
      let expiresAt = null;
      if (expiresInDays) {
        const expDate = new Date();
        expDate.setDate(expDate.getDate() + parseInt(expiresInDays));
        expiresAt = expDate.toISOString();
      }

      // The database record should already be created by the function,
      // just update it with QR code and additional fields if needed
      const { data, error } = await supabase
        .from('team_access_codes')
        .update({
          qr_code_data: qrCodeData,
          expires_at: expiresAt,
          max_uses: maxUses ? parseInt(maxUses) : null
        })
        .eq('access_code', newCode)
        .eq('team_id', teamId)
        .eq('is_active', true)
        .select()
        .single();

      if (error) {
        console.error('Error updating access code:', error);
        // If update fails, create a minimal object for display
        setAccessCode({
          id: crypto.randomUUID(),
          team_id: teamId,
          access_code: newCode,
          qr_code_data: qrCodeData,
          created_by: 'current_user',
          is_active: true,
          expires_at: expiresAt,
          max_uses: maxUses ? parseInt(maxUses) : null,
          current_uses: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      } else {
        setAccessCode(data);
      }

      console.log('Access code generated successfully:', newCode);
    } catch (error: any) {
      console.error('Error generating access code:', error);
      toast.error('Failed to generate access code: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    if (accessCode) {
      await navigator.clipboard.writeText(accessCode.access_code);
      toast.success('Access code copied to clipboard!');
    }
  };

  const downloadQRCode = () => {
    if (accessCode?.qr_code_data) {
      const link = document.createElement('a');
      link.download = `${teamName}-team-access-qr.png`;
      link.href = accessCode.qr_code_data;
      link.click();
    }
  };

  const regenerateCode = async () => {
    if (accessCode) {
      setLoading(true);
      try {
        // Deactivate old code
        await supabase
          .from('team_access_codes')
          .update({ is_active: false })
          .eq('id', accessCode.id);

        // Generate new code
        await generateAccessCode();
      } catch (error: any) {
        console.error('Error regenerating code:', error);
        toast.error('Failed to regenerate code: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <QrCode className="h-5 w-5" />
          Team Access Code Generator
        </CardTitle>
        <CardDescription>
          Generate a unique access code for players to join your team
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!accessCode ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="maxUses">Max Uses (Optional)</Label>
                <Input
                  id="maxUses"
                  type="number"
                  placeholder="Unlimited"
                  value={maxUses}
                  onChange={(e) => setMaxUses(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="expiresInDays">Expires in Days (Optional)</Label>
                <Input
                  id="expiresInDays"
                  type="number"
                  placeholder="No expiration"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                />
              </div>
            </div>
            <Button 
              onClick={generateAccessCode} 
              disabled={loading}
              className="w-full"
            >
              {loading ? 'Generating...' : 'Generate Access Code'}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="text-3xl font-mono font-bold tracking-wider bg-muted p-4 rounded-lg">
                {accessCode.access_code}
              </div>
              <div className="flex justify-center gap-2">
                <Button onClick={copyCode} variant="outline" size="sm">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Code
                </Button>
                <Button onClick={regenerateCode} variant="outline" size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Regenerate
                </Button>
              </div>
            </div>

            {accessCode.qr_code_data && (
              <div className="text-center space-y-4">
                <Label className="text-lg font-semibold">QR Code</Label>
                <div className="flex justify-center">
                  <img 
                    src={accessCode.qr_code_data} 
                    alt="Team Access QR Code"
                    className="w-48 h-48 border rounded-lg"
                  />
                </div>
                <Button onClick={downloadQRCode} variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Download QR Code
                </Button>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
              <div>
                <strong>Uses:</strong> {accessCode.current_uses}
                {accessCode.max_uses ? ` / ${accessCode.max_uses}` : ' / Unlimited'}
              </div>
              <div>
                <strong>Expires:</strong> {accessCode.expires_at 
                  ? new Date(accessCode.expires_at).toLocaleDateString()
                  : 'Never'
                }
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};